import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  UserIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  LockClosedIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'

export default function AdminUsers() {
  const { user } = useAuth()
  const [adminUsers, setAdminUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [filter, setFilter] = useState('all')

  // Check if current user is super admin
  const isSuperAdmin = user?.userType === 'admin' && (user?.is_superuser || user?.role === 'super_admin')

  useEffect(() => {
    loadAdminUsers()
  }, [filter])

  const loadAdminUsers = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('auth_token')
      const params = new URLSearchParams()
      if (filter !== 'all') params.append('status', filter)

      const response = await fetch(`/api/v1/admin/admin-users?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Failed to load admin users')

      const data = await response.json()
      setAdminUsers(data.admin_users || [])
    } catch (err) {
      console.error('Failed to load admin users:', err)
      setError('Failed to load admin users')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateUser = async (formData) => {
    try {
      setActionLoading(true)
      const token = localStorage.getItem('auth_token')

      const response = await fetch('/api/v1/admin/admin-users', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Failed to create admin user')
      }

      await loadAdminUsers()
      setShowCreateModal(false)
      alert('Admin user created successfully!')
    } catch (err) {
      console.error('Failed to create admin user:', err)
      alert(err.message)
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteUser = async (adminUser) => {
    if (!confirm(`Are you sure you want to delete ${adminUser.full_name}? This action cannot be undone.`)) return

    try {
      setActionLoading(true)
      const token = localStorage.getItem('auth_token')

      const response = await fetch(`/api/v1/admin/admin-users/${adminUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) throw new Error('Failed to delete admin user')

      await loadAdminUsers()
      setSelectedUser(null)
      alert('Admin user deleted successfully!')
    } catch (err) {
      console.error('Failed to delete admin user:', err)
      alert('Failed to delete admin user')
    } finally {
      setActionLoading(false)
    }
  }

  const getStatusBadge = (adminUser) => {
    if (adminUser.is_locked) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <LockClosedIcon className="w-3 h-3 mr-1" />
        Locked
      </span>
    }
    if (adminUser.is_active) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircleIcon className="w-3 h-3 mr-1" />
        Active
      </span>
    }
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
      <XCircleIcon className="w-3 h-3 mr-1" />
      Inactive
    </span>
  }

  const getRoleBadge = (role, is_superuser) => {
    if (role === 'super_admin' || is_superuser) {
      return <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
        <ShieldCheckIcon className="w-3 h-3 mr-1" />
        Super Admin
      </span>
    }
    return <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
      <UserIcon className="w-3 h-3 mr-1" />
      Admin
    </span>
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <LockClosedIcon className="mx-auto h-12 w-12 text-slate-400" />
          <h3 className="mt-2 text-sm font-medium text-slate-900">Access Restricted</h3>
          <p className="mt-1 text-sm text-slate-500">Only super administrators can manage admin users.</p>
        </div>
      </div>
    )
  }

  if (loading && adminUsers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-slate-200 border-t-brand-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Admin User Management</h1>
          <p className="mt-1 text-sm text-slate-600">
            Manage CognaAssist staff administrator accounts
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Create Admin User
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl border border-slate-200">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-slate-700">Status:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-1 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="all">All Users</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Admin Users Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Admin User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Login Stats
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {adminUsers.map((adminUser) => (
              <tr key={adminUser.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center">
                        <UserIcon className="h-5 w-5 text-slate-500" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-slate-900">{adminUser.full_name}</div>
                      <div className="text-sm text-slate-500">{adminUser.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getRoleBadge(adminUser.role, adminUser.is_superuser)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(adminUser)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                  <div>
                    <div className="text-sm">Logins: {adminUser.login_count || 0}</div>
                    <div className="text-xs text-slate-500">
                      Last: {adminUser.last_login_at
                        ? new Date(adminUser.last_login_at).toLocaleDateString()
                        : 'Never'
                      }
                    </div>
                    {adminUser.failed_login_attempts > 0 && (
                      <div className="text-xs text-red-600">
                        Failed attempts: {adminUser.failed_login_attempts}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                  {adminUser.created_at ? new Date(adminUser.created_at).toLocaleDateString() : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => setSelectedUser(adminUser)}
                    className="text-brand-600 hover:text-brand-900 p-1"
                    title="View Details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  {adminUser.id !== user?.id && (
                    <button
                      onClick={() => handleDeleteUser(adminUser)}
                      disabled={actionLoading}
                      className="text-red-600 hover:text-red-900 px-2 py-1 text-xs"
                      title="Delete"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {adminUsers.length === 0 && !loading && (
          <div className="text-center py-12">
            <UserIcon className="mx-auto h-12 w-12 text-slate-400" />
            <h3 className="mt-2 text-sm font-medium text-slate-900">No admin users found</h3>
            <p className="mt-1 text-sm text-slate-500">
              Get started by creating the first admin user.
            </p>
          </div>
        )}
      </div>

      {/* Create Admin User Modal */}
      {showCreateModal && (
        <CreateAdminUserModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateUser}
          loading={actionLoading}
        />
      )}

      {/* Admin User Details Modal */}
      {selectedUser && (
        <AdminUserDetailsModal
          adminUser={selectedUser}
          onClose={() => setSelectedUser(null)}
          currentUserId={user?.id}
        />
      )}
    </div>
  )
}

function CreateAdminUserModal({ onClose, onSubmit, loading }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    role: 'admin'
  })

  const handleSubmit = (e) => {
    e.preventDefault()

    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match')
      return
    }

    if (formData.password.length < 8) {
      alert('Password must be at least 8 characters long')
      return
    }

    const { confirmPassword, ...submitData } = formData
    onSubmit(submitData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 w-96 shadow-xl rounded-2xl border border-slate-200 bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-slate-900">Create Admin User</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-xl shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">First Name</label>
                <input
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-xl shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Last Name</label>
                <input
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-xl shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-xl shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Password</label>
              <input
                type="password"
                required
                minLength={8}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-xl shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Confirm Password</label>
              <input
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-xl shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Admin User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

function AdminUserDetailsModal({ adminUser, onClose, currentUserId }) {
  return (
    <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 w-96 shadow-xl rounded-2xl border border-slate-200 bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-slate-900">Admin User Details</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-3 text-sm">
            <div>
              <span className="font-medium text-slate-700">Name:</span>
              <span className="ml-2 text-slate-900">{adminUser.full_name}</span>
            </div>
            <div>
              <span className="font-medium text-slate-700">Email:</span>
              <span className="ml-2 text-slate-900">{adminUser.email}</span>
            </div>
            <div>
              <span className="font-medium text-slate-700">Role:</span>
              <span className="ml-2">{getRoleBadge(adminUser.role, adminUser.is_superuser)}</span>
            </div>
            <div>
              <span className="font-medium text-slate-700">Status:</span>
              <span className="ml-2">{getStatusBadge(adminUser)}</span>
            </div>
            <div>
              <span className="font-medium text-slate-700">Login Count:</span>
              <span className="ml-2 text-slate-900">{adminUser.login_count || 0}</span>
            </div>
            <div>
              <span className="font-medium text-slate-700">Last Login:</span>
              <span className="ml-2 text-slate-900">
                {adminUser.last_login_at
                  ? new Date(adminUser.last_login_at).toLocaleString()
                  : 'Never'
                }
              </span>
            </div>
            <div>
              <span className="font-medium text-slate-700">Created:</span>
              <span className="ml-2 text-slate-900">
                {adminUser.created_at
                  ? new Date(adminUser.created_at).toLocaleString()
                  : 'Unknown'
                }
              </span>
            </div>
            {adminUser.failed_login_attempts > 0 && (
              <div>
                <span className="font-medium text-slate-700">Failed Login Attempts:</span>
                <span className="ml-2 text-red-600">{adminUser.failed_login_attempts}</span>
              </div>
            )}
            {adminUser.id === currentUserId && (
              <div className="mt-4 p-3 bg-brand-50 rounded-md">
                <p className="text-brand-800 text-sm">
                  <ShieldCheckIcon className="inline w-4 h-4 mr-1" />
                  This is your current account
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper functions (duplicated for modal use)
function getStatusBadge(adminUser) {
  if (adminUser.is_locked) {
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
      <LockClosedIcon className="w-3 h-3 mr-1" />
      Locked
    </span>
  }
  if (adminUser.is_active) {
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
      <CheckCircleIcon className="w-3 h-3 mr-1" />
      Active
    </span>
  }
  return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
    <XCircleIcon className="w-3 h-3 mr-1" />
    Inactive
  </span>
}

function getRoleBadge(role, is_superuser) {
  if (role === 'super_admin' || is_superuser) {
    return <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
      <ShieldCheckIcon className="w-3 h-3 mr-1" />
      Super Admin
    </span>
  }
  return <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
    <UserIcon className="w-3 h-3 mr-1" />
    Admin
  </span>
}
